// src/types/formBuilder.ts

import { RulesConfig } from "./Forms";

// --- Base Types ---
export type OptionType = string;
export type OptionWithValueType = { value: string; label: string };
export type OptionContentType = OptionType | OptionWithValueType;

// Define operators - can keep stricter for UI, but map to string if needed
export type Operator =
  | 'EQUALS'
  | 'INCLUDES'
  | 'CONTAINS'
  | 'CONTAINS_ANY'
  | 'DOES_NOT_CONTAIN'
  | 'DOES_NOT_CONTAIN_ANY'
  | 'GREATER_THAN'
  | 'LESS_THAN'
  // Add any other operators used in your runtime logic
  | string; // Allow any string to match runtime flexibility if needed

// Define known dynamic sources
export type DynamicSourceType = 'participants' | 'taskContactTypes' | 'waiverTypes';
export const KNOWN_DYNAMIC_SOURCES: DynamicSourceType[] = ['participants', 'taskContactTypes', 'waiverTypes'];

// --- Interfaces based on "Forms Interfaces" ---

// Matches Forms Interfaces: Condition / ConditionDefinition
export interface Condition {
  field: string;
  // Use the stricter Operator type for UI, but backend might expect string
  operator: Operator;
  value: any; // Match runtime 'any', handle specific input types in UI
}

// Matches Forms Interfaces: QuestionType
export type QuestionType =
  | "text"
  | "checkbox"
  | "radio"
  | "react-select"
  | "date"
  | "time"
  | "datetime"
  | "currency"
  | "tel"
  | "number"
  | "paragraph"
  | "display"; // Added display type

// Matches Forms Interfaces: QuestionnaireItem
export interface QuestionnaireItem {
  id: string;
  label: string;
  type: QuestionType;
  options?: OptionContentType[]; // Use unified OptionContentType
  isMulti?: boolean;
  showIf?: Condition[];
  hideIf?: Condition[]; // Added from Forms Interfaces
  placeholder?: string;
  minNumber?: number; // Added from Forms Interfaces
  maxNumber?: number; // Added from Forms Interfaces
  stepNumber?: number; // Added from Forms Interfaces
  currencySymbol?: string; // Added from Forms Interfaces
  prefix?: string;
  format?: string;
  maxLength?: number;
  // Use specific type for builder, ensure compatibility with runtime "participants"
  dynamicSource?: DynamicSourceType | '';
  disabled?: boolean;
  validation?: {
    required?: boolean;
    // Add other simple validations if needed in builder & runtime
  };
}

// Matches Forms Interfaces: WizardStep
export interface WizardStep {
  id: string;
  label: string;
  icon?: string;
  questions: QuestionnaireItem[];
}

// Matches Forms Interfaces: FormConfig (Represents the Definition part)
export interface FormDefinition {
  formId: string;
  steps: WizardStep[];
}

export interface FormConfig { 
  definition: FormDefinition, 
  rules: RulesConfig, 
}

// Matches Forms Interfaces: CheckDefinition
export interface CheckDefinition {
  // Allow flexible structure from Forms Interfaces
  type?: string; // e.g., "TIME_DIFF" or implied "FIELD_CHECK"
  field?: string;
  operator?: Operator; // Use stricter type for UI?
  value?: any;
  startField?: string;
  endField?: string;
  minMinutes?: number;
}

// Matches Forms Interfaces: ValidationRule
export interface ValidationRule {
  id: string;
  description?: string; // Optional to match Forms Interfaces
  when?: Condition[]; // Use unified Condition type
  checks: CheckDefinition[]; // Use CheckDefinition structure
  errorMessage: string;
  errorField?: string; // Optional to match Forms Interfaces
}

// Matches Forms Interfaces: RulesConfig (Represents the Rules part)
export interface FormValidationRules {
  formId: string;
  validationRules: ValidationRule[];
}

export interface PdfMapping {
  TextFieldMappings?: { [questionId: string]: string };
  CheckBoxMappings?: { 
      [questionId: string]: { Yes: string; No: string }
  };
  // Define other mapping types here as needed...
}

// --- Update the FormConfig interface ---
export interface FormConfig { 
definition: FormDefinition, 
rules: FormValidationRules; // Using the more specific type from your file
pdfMapping?: PdfMapping;    // ADD THIS LINE
}

// --- Constants for Builder UI ---

// Update with all types from Forms Interfaces
export const QUESTION_TYPES: QuestionType[] = [
    "text",
    "checkbox",
    "radio",
    "react-select",
    "date",
    "time",
    "datetime",
    "currency",
    "tel",
    "number",
    "paragraph",
    "display"
];

// Can keep stricter operator list for UI dropdowns
export const UI_OPERATORS: Operator[] = [
    'EQUALS',
    'INCLUDES',
    'CONTAINS',
    'CONTAINS_ANY',
    'DOES_NOT_CONTAIN',
    'DOES_NOT_CONTAIN_ANY',
    'GREATER_THAN',
    'LESS_THAN',
    'GREATER_THAN_OR_EQUAL',
    'LESS_THAN_OR_EQUAL',
    'IS_EMPTY',
    'IS_NOT_EMPTY'
];


// --- Helper Functions (Keep Existing) ---

export const getAllQuestionIds = (definition?: FormDefinition | null): string[] => {
    if (!definition) return [];
    return definition.steps.flatMap(step => step.questions.map(q => q.id));
};

let idCounter = Date.now();
export const generateUniqueId = (prefix: string = 'item'): string => {
  idCounter++;
  return `${prefix}-${idCounter}`;
};